import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { config } from '@/services/config';
import { get, map, omitBy, orderBy } from 'lodash';
import { formatDataValue } from '@/helpers/format';
import {
  ClientTableData,
  ClientHeatmapData,
  ClientHeatmapDataRange,
  ClientHeatmapDataRangeItem,
} from '@/types/Client';
import { BookApiOptions, HeatmapSliderOptions } from './types';
import { RouteName } from '@/enums/Routes';
import { createTableHeaders, createTopTableHeaders } from '@/helpers/tables';
import { translations } from '@/plugins/localisation';
import { AggregateBy } from '@/enums/Global';
import { getFeatureFlag, FeatureFlag } from '@/services/config';
import { DataValue } from '@/types/Global';

export type Getters = {
  getBookList(
    state: State,
    options: {
      pageName: string;
      hidePricingReviewControl: boolean;
      aggregateBy: AggregateBy;
    }
  ): ClientTableData;
  isClientDataLoading(state: State): boolean;
  getHeatmapData(state: State): ClientHeatmapData;
  isHeatmapDataLoading(state: State): boolean;
  getSliderOptions(state: State): HeatmapSliderOptions;
  getBookSettings(state: State): BookApiOptions;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getBookList: (state, options) => {
    const clientData = state.client.clientData;
    const clientTableConfig = get(
      config.layout,
      'tableLayouts.clientTable',
      {}
    );
    const configTopHeaders = get(clientTableConfig, 'topHeaders', []);
    let configHeaders = get(clientTableConfig, 'mainHeaders', []);

    if (
      options.pageName === RouteName.BANKER_VIEW ||
      options.pageName === RouteName.BANKER_VIEW_WITH_ID
    ) {
      configHeaders = omitBy(configHeaders, { key: 'bankerId' });
    }

    if (
      options.hidePricingReviewControl ||
      options.aggregateBy === AggregateBy.FAMILY
    ) {
      configHeaders = omitBy(configHeaders, { key: 'checkboxReview' });
    }

    if (
      !getFeatureFlag(FeatureFlag.FAMILY_LEVEL_SIMULATED_IMPACT).enabled ||
      (options.aggregateBy === AggregateBy.FAMILY &&
        getFeatureFlag(FeatureFlag.FAMILY_LEVEL_SIMULATED_IMPACT).enabled)
    ) {
      configHeaders = omitBy(configHeaders, { key: 'aggregatedFamilyStatus' });
    }

    if (options.aggregateBy === AggregateBy.FAMILY) {
      configHeaders = omitBy(configHeaders, { key: 'clientId' });
      configHeaders = omitBy(configHeaders, { key: 'statusClient' });
      configHeaders = omitBy(configHeaders, { key: 'statusPortfolio' });
    }

    if (options.aggregateBy === AggregateBy.CLIENT) {
      configHeaders = omitBy(configHeaders, { key: 'statusFamily' });
      configHeaders = omitBy(configHeaders, { key: 'statusPortfolio' });
    }

    const items = map(clientData, item => {
      let client = {};
      const nestedValueKeys = ['clientSpecificValues', 'aggregate'];

      for (const key in item) {
        if (!nestedValueKeys.includes(key)) {
          client[key] = item[key];
        }
      }

      client = {
        ...client,
        ...item.aggregate,
        ...item.clientSpecificValues,
      };

      const formattedValues: { [x: string]: string | number | boolean } = {};

      for (const [key, value] of Object.entries(client)) {
        let formattedValue;

        if (key === 'roa' && (value as number) > 0.1) {
          formattedValue = translations.global.t('general.NOT_DISCLOSED');
        } else {
          formattedValue = formatDataValue(value as DataValue);
        }

        formattedValues[key] = formattedValue;
      }

      formattedValues['aggregatedFamilyStatus'] = formattedValues.familyStatus;
      formattedValues['statusFamily'] = formattedValues.status;
      formattedValues['statusClient'] = formattedValues.status;
      formattedValues['statusPortfolio'] = formattedValues.status;

      return formattedValues;
    });

    return {
      items: items,
      headers: createTableHeaders(configHeaders),
      topHeaders: createTopTableHeaders(configTopHeaders),
      totalResultCount: get(state.client, 'totalResultCount', 0),
    };
  },
  isClientDataLoading: state => {
    return state.client.isLoading;
  },
  getHeatmapData: state => {
    const data = state.heatmap.heatmapData;

    const heatmapSliderConfig = get(
      config.layout,
      'bookView.heatmap.sliders',
      {}
    );
    const range = Object.keys(heatmapSliderConfig).reduce(
      (acc: ClientHeatmapDataRange, axis: string) => ({
        ...acc,
        [heatmapSliderConfig[axis].key]: {} as ClientHeatmapDataRangeItem,
      }),
      {} as ClientHeatmapDataRange
    );

    if (data && data.length) {
      Object.keys(range).forEach((key: string) => {
        const dataOrderedByKey = orderBy(data, [`${key}.dataValue`]);
        range[key].min = get(dataOrderedByKey[0], `key.dataValue`, 0);
        range[key].max = get(
          dataOrderedByKey[dataOrderedByKey.length - 1],
          `${key}.dataValue`,
          1
        );
      });
    }

    return {
      data,
      range,
    } as ClientHeatmapData;
  },
  isHeatmapDataLoading: state => {
    return state.heatmap.isLoading;
  },
  getSliderOptions: state => {
    return state.heatmap.heatmapSliderOptions;
  },
  getBookSettings: state => {
    return state.settings;
  },
};
